import {
    FC,
    useCallback,
    useMemo,
    useRef,
    useState,
    lazy,
    Suspense,
    useLayoutEffect
} from "react"
import { useDispatch } from "react-redux"
import {
    KeyboardShortcut,
    PrelabelResponse,
    UpdateReturn,
    hasClassificationFieldSchema
} from "resha"
import { LabelSource, UniversalSchemaType } from "universal-schema/lib/0.0.4"
import { Loader } from "dash-styled"
import { getJobResponse } from "../../../../../../Actions/API/Job/JobActions"
import { isValidObject } from "../../../../../../Helpers/Functions/object"
import { useAsyncCommand } from "../../../../../../Hooks/useAsyncCommand"
import { APIAppInstanceModel } from "../../../../../../Models/API/AppInstanceModel"
import {
    APIJobModel,
    JobStatus,
    JobType,
    JobVersionItem
} from "../../../../../../Models/API/JobModel"
import { findAnnotationOutput } from "../../../../../../Models/Client/AnnotationOutputSchemaItem"
import { PartialBy } from "../../../../../../Models/Utility/PartialBy"
import { MainThunkDispatch } from "../../../../../../Reducers"
import { CodeBlock } from "../../../Common/CodeBlock/CodeBlock"
import { ActionButton } from "../../../../../Common/ActionButton/ActionButton"
import {
    hasAppPermission,
    ProjectRole
} from "../../../../../../Models/Client/ProjectRole"
import { AnnotationOutputSchemaItem, SchemaForm } from "universal-schema/lib"
import { AnnotationViewer } from "../../../../../Common/Lazy/AnnotationViewer"
import { InputFormRender } from "../../../../../Common/InputFormRender/InputFormRender"
import { possibleOutputFormats } from "../Common/constants"
import { useJobPostProcessedData } from "../../../../../../Hooks/useJobPostProcessedData"
import { PostProcessedOutput } from "../../../../../../Models/DataProgram/TemplateMetadata"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import { selectUserName } from "../../../../../../Helpers/Functions/Selectors/auth"

const ShortcutManagerContainer = lazy(async () => {
    return import("../../../../../Common/Lazy/ShortcutManagerContainer").then(
        shortcutContainer => ({
            default: shortcutContainer.ShortcutManagerContainer
        })
    )
})

interface JobDetailInputOutputProps {
    isReviewView: boolean
    mode: "input" | "output"
    job: APIJobModel
    instance: APIAppInstanceModel
    inputSchema: SchemaForm
    outputSchema: SchemaForm
    selectedVersion: JobVersionItem
    outputFormatType: typeof possibleOutputFormats[number]
    role: ProjectRole[]
    showOldVersion: boolean
    prelabels: PrelabelResponse
    postProcessedData: PostProcessedOutput
    postProcessTitle?: string
    onEnqueue?: () => Promise<void>
    onRelabel?: () => Promise<void>
    handleCancel?: () => Promise<void>
    urlSigner: (url: string) => Promise<string>
    onRequestTaskDetail?: (taskId: number) => void
    onItemChange?: (item: UpdateReturn<unknown> | undefined) => void
    onReviewChange?: () => void
    onEditAttempt?: () => void
    onShortcutList?: (shortcutList: {
        [key: string]: [string, KeyboardShortcut][]
    }) => void
}

export const JobDetailInputOutput: FC<JobDetailInputOutputProps> = ({
    isReviewView,
    mode,
    job,
    instance,
    inputSchema,
    outputSchema,
    selectedVersion,
    outputFormatType,
    role,
    showOldVersion,
    prelabels,
    postProcessedData,
    postProcessTitle,
    onEnqueue,
    onRelabel,
    handleCancel,
    urlSigner,
    onRequestTaskDetail,
    onItemChange,
    onReviewChange,
    onEditAttempt,
    onShortcutList
}) => {
    const ref = useRef<HTMLDivElement>(null)
    const [measuredHeight, setMeasuredHeight] = useState<number>()
    const myUsername = useAppState(selectUserName)
    const dispatch = useDispatch<MainThunkDispatch>()
    const refresh = useAsyncCommand(() => dispatch(getJobResponse(job.id)))
    const { formData: postProcessedOutput, schema: postProcessedSchema } =
        useJobPostProcessedData(
            inputSchema,
            selectedVersion.responseData,
            postProcessTitle
        )

    const annotationItem = useMemo(
        () =>
            findAnnotationOutput(
                outputSchema,
                selectedVersion.status,
                selectedVersion.response
            ),
        [outputSchema, selectedVersion]
    )

    const layout = useMemo(
        () =>
            outputFormatType !== "json" &&
            annotationItem !== undefined &&
            annotationItem.type !== UniversalSchemaType.TOKEN_ANNOTATION
                ? "fit-container"
                : "stacked",
        [annotationItem, outputFormatType]
    )
    const isCollaboratorJob = useMemo(
        () => job.jobType === JobType.COLLABORATOR,
        [job.jobType]
    )

    const handleEnqueue = useAsyncCommand(onEnqueue)
    const handleRelabel = useAsyncCommand(onRelabel)
    const handleCancelAsync = useAsyncCommand(handleCancel)

    const renderInput = (format: "json" | "formatted" | "processed") => {
        if (format === "json") {
            return <CodeBlock className="h-full">{job.input}</CodeBlock>
        }

        return isValidObject(inputSchema) && isValidObject(job.input) ? (
            <InputFormRender
                {...inputSchema}
                formData={job.input}
                skipMissingDataField={false}
                documentMaxHeight={
                    measuredHeight ? `${measuredHeight - 60}px` : undefined
                }
            />
        ) : (
            <>Unknown input</>
        )
    }

    const renderOutputNotReady = () => (
        <ActionButton
            text="Output isn't here quite yet"
            action={refresh.perform}
            buttonText={refresh.isProcessing ? "Refreshing..." : "Refresh"}
            disabled={refresh.perform === undefined || refresh.isProcessing}
        />
    )

    const isInstanceDefined = useCallback(
        (
            item: PartialBy<AnnotationOutputSchemaItem, "schema_instance">
        ): item is AnnotationOutputSchemaItem => {
            return item.schema_instance !== undefined
        },
        []
    )
    const handleRequestLabelSourceDetail = useCallback(
        (source: LabelSource) => {
            if (source.type === "task" && onRequestTaskDetail !== undefined) {
                onRequestTaskDetail(source.id)
            }
        },
        [onRequestTaskDetail]
    )

    const renderAnnotation = (
        annotationItem: PartialBy<AnnotationOutputSchemaItem, "schema_instance">
    ) => {
        if (isInstanceDefined(annotationItem)) {
            return (
                <div
                    className={
                        outputFormatType === "formatted" ? "h-full" : "hidden"
                    }
                >
                    <AnnotationViewer
                        mode={isReviewView ? "review" : "readOnly"}
                        username={myUsername}
                        showVideoFramePane={true}
                        key={selectedVersion.versionName}
                        appParams={instance.appParams}
                        output={annotationItem}
                        urlSigner={urlSigner}
                        prelabels={prelabels}
                        prelabelsInitialVisibility={
                            job.status !== JobStatus.COMPLETED
                        }
                        onRequestLabelSourceDetail={
                            handleRequestLabelSourceDetail
                        }
                        fallback={
                            <Loader size="xxxl" absolute={true} mask={false} />
                        }
                        enableShortcuts={false}
                        onItemChange={onItemChange}
                        onReviewChange={onReviewChange}
                        onEditAttempt={onEditAttempt}
                        onShortcutList={onShortcutList}
                    />
                </div>
            )
        }

        return renderOutputNotReady()
    }

    const renderSchemaOutput = (
        format: "json" | "formatted" | "processed"
    ): JSX.Element => {
        switch (selectedVersion?.status) {
            case JobStatus.COMPLETED:
                if (selectedVersion.response === undefined) {
                    return renderOutputNotReady()
                }

                if (
                    format === "processed" &&
                    (postProcessedOutput === undefined ||
                        postProcessedOutput === "")
                ) {
                    return (
                        <ActionButton
                            hideButton
                            text="Output isn't here quite yet"
                        />
                    )
                }

                if (format === "json") {
                    return (
                        <CodeBlock className="h-full">
                            {selectedVersion.response}
                        </CodeBlock>
                    )
                }

                if (format === "processed") {
                    return !selectedVersion.responseData ? (
                        <ActionButton
                            text="Output isn’t here quite yet"
                            action={refresh.perform}
                            buttonText={
                                refresh.isProcessing
                                    ? "Refreshing..."
                                    : "Refresh"
                            }
                            disabled={
                                refresh.perform === undefined ||
                                refresh.isProcessing
                            }
                        />
                    ) : (
                        <InputFormRender
                            key={`${job.uuid}_output_post_processed_${selectedVersion.versionName}`}
                            className="h-full"
                            formData={postProcessedOutput}
                            {...postProcessedSchema}
                            skipMissingDataField={false}
                            documentMaxHeight={
                                measuredHeight
                                    ? `${measuredHeight - 60}px`
                                    : undefined
                            }
                        />
                    )
                }
                return !selectedVersion.response ? (
                    <ActionButton
                        text="Output isn’t here quite yet"
                        action={refresh.perform}
                        buttonText={
                            refresh.isProcessing ? "Refreshing..." : "Refresh"
                        }
                        disabled={
                            refresh.perform === undefined ||
                            refresh.isProcessing
                        }
                    />
                ) : (
                    <InputFormRender
                        key={`${job.uuid}_output_${selectedVersion.versionName}`}
                        className="h-full"
                        formData={selectedVersion.response}
                        {...outputSchema}
                        prelabels={prelabels}
                        skipMissingDataField={false}
                        documentMaxHeight={
                            measuredHeight
                                ? `${measuredHeight - 60}px`
                                : undefined
                        }
                    />
                )
            case JobStatus.PENDING:
                return (
                    <ActionButton
                        hideButton={
                            !hasAppPermission(role, "job:submit") ||
                            showOldVersion
                        }
                        text={`This ${
                            showOldVersion ? "version of the " : ""
                        }data point ${showOldVersion ? "was" : "is"} parked`}
                        action={handleEnqueue.perform}
                        buttonText={
                            handleEnqueue.isProcessing
                                ? "Queuing..."
                                : "Queue for processing"
                        }
                        disabled={
                            handleEnqueue.perform === undefined ||
                            handleEnqueue.isProcessing
                        }
                    />
                )
            case JobStatus.CANCELED:
                return (
                    <ActionButton
                        hideButton={
                            !hasAppPermission(role, "job:relabel") ||
                            showOldVersion
                        }
                        text={`This ${
                            showOldVersion ? "version of the " : ""
                        }data point ${
                            showOldVersion ? "was" : "has been"
                        } canceled`}
                        action={handleRelabel.perform}
                        buttonText={
                            handleRelabel.isProcessing
                                ? "Sending for reprocess..."
                                : "Send for reprocessing"
                        }
                        disabled={
                            handleRelabel.perform === undefined ||
                            handleRelabel.isProcessing
                        }
                    />
                )
            case JobStatus.EXPIRED:
                return (
                    <ActionButton
                        hideButton={
                            !hasAppPermission(role, "job:relabel") ||
                            showOldVersion
                        }
                        text={`This ${
                            showOldVersion ? "version of the " : ""
                        }data point is expired`}
                        action={handleRelabel.perform}
                        buttonText={
                            handleRelabel.isProcessing
                                ? "Sending for reprocess..."
                                : "Send for reprocessing"
                        }
                        disabled={
                            handleRelabel.perform === undefined ||
                            handleRelabel.isProcessing
                        }
                    />
                )
            case JobStatus.FAILED:
                return (
                    <ActionButton
                        hideButton={
                            !hasAppPermission(role, "job:relabel") ||
                            showOldVersion
                        }
                        text={`This ${
                            showOldVersion ? "version of the " : ""
                        }data point is failed`}
                        action={handleRelabel.perform}
                        buttonText={
                            handleRelabel.isProcessing
                                ? "Sending for reprocess..."
                                : "Send for reprocessing"
                        }
                        disabled={
                            handleRelabel.perform === undefined ||
                            handleRelabel.isProcessing
                        }
                    />
                )
            case JobStatus.IN_PROGRESS:
            case JobStatus.INTERNAL_ERROR:
                return (
                    <ActionButton
                        hideButton={
                            !hasAppPermission(role, "job:cancel") ||
                            showOldVersion
                        }
                        text={`This data point is ${
                            isCollaboratorJob
                                ? "ready to process"
                                : "processing"
                        }`}
                        action={handleCancelAsync.perform}
                        buttonText={
                            handleCancelAsync.isProcessing
                                ? "Canceling..."
                                : "Cancel"
                        }
                        disabled={
                            handleCancelAsync.perform === undefined ||
                            handleCancelAsync.isProcessing
                        }
                    />
                )
            case JobStatus.SCHEDULED:
            case JobStatus.ENQUEUED:
                return (
                    <ActionButton
                        hideButton={
                            !hasAppPermission(role, "job:cancel") ||
                            showOldVersion
                        }
                        text={`This ${
                            showOldVersion ? "version of the " : ""
                        }data point ${showOldVersion ? "was" : "is"} queued`}
                        action={handleCancelAsync.perform}
                        buttonText={
                            handleCancelAsync.isProcessing
                                ? "Canceling..."
                                : "Cancel"
                        }
                        disabled={
                            handleCancelAsync.perform === undefined ||
                            handleCancelAsync.isProcessing
                        }
                    />
                )
            case JobStatus.SUSPENDED:
                return (
                    <ActionButton
                        hideButton={showOldVersion}
                        text={`This ${
                            showOldVersion ? "version of the " : ""
                        }data point ${showOldVersion ? "was" : "is"} paused`}
                    />
                )
            case JobStatus.IN_CREATION:
                return (
                    <div className="flex flex-col text-center py-18 h-68">
                        <Loader size="xxxl" />
                        <p
                            className="flex-grow text-smoke-850 leading-xxxl"
                            data-testid="in-creation-output-label"
                        >
                            This version is being created
                        </p>
                    </div>
                )
        }
    }

    const renderOutput = () => {
        if (annotationItem && outputFormatType === "formatted") {
            return renderAnnotation(annotationItem)
        } else {
            return renderSchemaOutput(outputFormatType)
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        setMeasuredHeight(ref.current?.offsetHeight)
    })

    const hasClassification = hasClassificationFieldSchema(
        outputSchema.uiSchema
    )

    return (
        <Suspense fallback={<Loader size="xxxl" />}>
            <ShortcutManagerContainer>
                <div
                    ref={ref}
                    data-testid="job-detail-input-output"
                    className={`relative flex-grow pb-4 overflow-auto ${
                        layout === "fit-container"
                            ? "flex-shrink h-0"
                            : "flex-shrink-0"
                    } ${hasClassification ? "h-full" : ""}`}
                >
                    {mode === "input" &&
                        !isReviewView &&
                        renderInput(outputFormatType)}
                    {mode === "output" && renderOutput()}
                </div>
            </ShortcutManagerContainer>
        </Suspense>
    )
}
